import React, { useEffect, useState, useCallback, useRef } from 'react';
import LoadingSpinner from '../shared/LoadingSpinner';
import { formatDateTime } from '../../utils/dateFormatters';
import {
    TabContent,
    DetailPanel,
    ListDetailLayout,
    ListPanel,
    EditActionsBar,
    FileUploadRow,
    HiddenFileInput,
    UploadButton,
    FileLink,
    SuccessMessage,
    ErrorMessage,
} from '../../pages/admin/AdminPage.styles';
import { EditableInput, EditableTextarea } from '../product/EditableFields.styles';
import { useEditMode } from '../../context/EditModeContext';
import { useAuthStore } from '../../context/AuthContext';
import useUpdateCoatingNote from '../../api/useUpdateCoatingNote';
import useGetCoatingManufacturers from '../../api/useGetCoatingManufacturers';
import { useUploadFile } from '../../api/useUploadFile';
import { useDownloadFile } from '../../api/useDownloadFile';
import { CoatingNote } from '../../api/useGetCoatingNotes';

interface CoatingNoteTabProps {
    coatingNotes: CoatingNote[];
    loading: boolean;
    error: string | null;
    selectedCoatingNote: CoatingNote | null;
    setSelectedCoatingNote: (note: CoatingNote | null) => void;
    onRefresh?: () => void;
}

const CoatingNoteTab: React.FC<CoatingNoteTabProps> = ({ coatingNotes, loading, error, selectedCoatingNote, setSelectedCoatingNote, onRefresh }) => {
    const { updateCoatingNote, isLoading: isUpdating } = useUpdateCoatingNote();
    const { isInEditMode, setEditMode } = useEditMode();
    const { loggedInUser } = useAuthStore();
    const { manufacturers, getCoatingManufacturers } = useGetCoatingManufacturers();
    const { uploadFile, isUploading, uploadError, uploadSuccess, clearMessages } = useUploadFile();
    const { downloadFile, getFileUrl, isDownloading } = useDownloadFile();
    const [formData, setFormData] = useState<CoatingNote | null>(null);
    const [updateError, setUpdateError] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const canEdit = loggedInUser !== null;

    // Load coating manufacturers on mount
    useEffect(() => {
        getCoatingManufacturers();
    }, [getCoatingManufacturers]);

    // Initialize form data when note is selected
    useEffect(() => {
        if (selectedCoatingNote) {
            setFormData(selectedCoatingNote);
            // Don't reset edit mode if this is a new item (id === 0)
            if (selectedCoatingNote.id !== 0) {
                setEditMode(false);
            }
        }
    }, [selectedCoatingNote, setEditMode]);

    // Handle edit mode
    const handleEdit = useCallback(() => {
        setEditMode(true);
        setUpdateError(null);
    }, [setEditMode]);

    // Handle add new coating note
    const handleAddNew = useCallback(() => {
        const newNote: CoatingNote = {
            id: 0,
            manufacturerId: 0,
            manufacturerName: '',
            isGeneralNote: false,
            number: '',
            text: '',
            filePath: '',
            lastUpdatedUtc: new Date().toISOString(),
        };
        setSelectedCoatingNote(newNote);
        setEditMode(true);
        setUpdateError(null);
    }, [setEditMode, setSelectedCoatingNote]);

    // Handle cancel
    const handleCancel = useCallback(() => {
        setFormData(selectedCoatingNote);
        setEditMode(false);
        setUpdateError(null);
    }, [selectedCoatingNote, setEditMode]);

    // Handle save
    const handleSave = useCallback(async () => {
        if (!formData) return;

        try {
            await updateCoatingNote(formData);
            setEditMode(false);
            setUpdateError(null);
            // Refresh the list to show updated data
            onRefresh?.();
        } catch (err: any) {
            setUpdateError(err.message);
        }
    }, [formData, updateCoatingNote, setEditMode, onRefresh]);

    // Handle field changes
    const handleFieldChange = useCallback((field: keyof CoatingNote, value: any) => {
        setFormData((prev) => {
            if (!prev) return prev;
            return {
                ...prev,
                [field]: value,
            };
        });
    }, []);

    // Handle file upload
    const handleFileUpload = useCallback(
        async (event: React.ChangeEvent<HTMLInputElement>) => {
            const file = event.target.files?.[0];
            if (!file) return;

            clearMessages();

            try {
                await uploadFile({
                    category: 'Coating',
                    fileName: file.name,
                    file: file,
                });

                // Update the filePath in form data after successful upload
                handleFieldChange('filePath', file.name);
            } catch (err) {
                console.error('Failed to upload file:', err);
            }

            // Reset file input
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        },
        [uploadFile, clearMessages, handleFieldChange]
    );

    // Trigger file input click
    const handleUploadClick = useCallback(() => {
        fileInputRef.current?.click();
    }, []);

    // Handle file download
    const handleDownload = useCallback(async () => {
        if (!formData?.filePath) return;
        try {
            await downloadFile({
                category: 'Coating',
                fileName: formData.filePath,
            });
        } catch (err) {
            console.error('Failed to download file:', err);
        }
    }, [formData?.filePath, downloadFile]);

    // Keyboard navigation
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (!coatingNotes.length) return;

            const currentIndex = coatingNotes.findIndex((n) => n.id === selectedCoatingNote?.id);

            switch (event.key) {
                case 'ArrowDown': {
                    event.preventDefault();
                    const nextIndex = currentIndex < coatingNotes.length - 1 ? currentIndex + 1 : 0;
                    setSelectedCoatingNote(coatingNotes[nextIndex]);
                    break;
                }
                case 'ArrowUp': {
                    event.preventDefault();
                    const prevIndex = currentIndex > 0 ? currentIndex - 1 : coatingNotes.length - 1;
                    setSelectedCoatingNote(coatingNotes[prevIndex]);
                    break;
                }
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [coatingNotes, selectedCoatingNote, setSelectedCoatingNote]);

    const getNoteTypeDisplay = (note: CoatingNote) => {
        return note.isGeneralNote ? 'General Note' : `Specific Note`;
    };

    return (
        <TabContent>
            <ListDetailLayout>
                <ListPanel>
                    {loading && <LoadingSpinner text="Loading coating notes..." />}

                    {error && (
                        <div className="error-state">
                            <i className="fas fa-exclamation-triangle"></i> Error loading coating notes: {error}
                        </div>
                    )}

                    {!loading && !error && coatingNotes.length === 0 && (
                        <div className="empty-state">
                            <i className="fas fa-sticky-note"></i>
                            <p>No coating notes found</p>
                        </div>
                    )}

                    {!loading && coatingNotes.length > 0 && (
                        <div className="table-container">
                            <table className="data-table">
                                <thead>
                                    <tr>
                                        <th>Manufacturer</th>
                                        <th>Type</th>
                                        <th>Number</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {coatingNotes.map((note) => (
                                        <tr
                                            key={note.id}
                                            className={`table-row ${selectedCoatingNote?.id === note.id ? 'selected' : ''}`}
                                            onClick={() => setSelectedCoatingNote(note)}
                                        >
                                            <td className="manufacturer-cell">{note.manufacturerName}</td>
                                            <td className="type-cell">{getNoteTypeDisplay(note)}</td>
                                            <td className="number-cell">{note.isGeneralNote ? '' : note.number}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </ListPanel>

                <DetailPanel>
                    {selectedCoatingNote && formData ? (
                        <>
                            {isInEditMode && (
                                <EditActionsBar>
                                    <button onClick={handleAddNew} className="btn-add-new" title="Add new coating note">
                                        <i className="fas fa-plus"></i> New Coating Note
                                    </button>
                                    <div>
                                        <button onClick={handleSave} className="btn-save" disabled={isUpdating} title="Save changes">
                                            <i className="fas fa-save"></i> {isUpdating ? 'Saving...' : 'Save'}
                                        </button>
                                        <button onClick={handleCancel} className="btn-cancel" disabled={isUpdating} title="Cancel changes">
                                            <i className="fas fa-times"></i> Cancel
                                        </button>
                                    </div>
                                </EditActionsBar>
                            )}
                            <div className="detail-content">
                                <div className="detail-header">
                                    <h3>
                                        {formData.isGeneralNote
                                            ? `${formData.manufacturerName} - General Note`
                                            : `${formData.manufacturerName} - Note ${formData.number}`}
                                    </h3>
                                    <div>
                                        <div className="header-actions">
                                            {canEdit && !isInEditMode && (
                                                <button onClick={handleEdit} className="edit-btn" title="Edit">
                                                    <i className="fas fa-pencil-alt"></i>
                                                </button>
                                            )}
                                        </div>
                                        {formData.lastUpdatedUtc && (
                                            <div className="last-updated">Last updated: {formatDateTime(formData.lastUpdatedUtc)}</div>
                                        )}
                                    </div>
                                </div>
                                {updateError && (
                                    <div className="error-alert">
                                        <i className="fas fa-exclamation-triangle"></i> {updateError}
                                    </div>
                                )}
                                <div className="detail-body">
                                    <div className="detail-section">
                                        <h4>Basic Information</h4>
                                        <div className="detail-grid">
                                            <div className="detail-item">
                                                <label>General Note</label>
                                                {isInEditMode ? (
                                                    <select
                                                        value={formData.isGeneralNote ? 'true' : 'false'}
                                                        onChange={(e) => handleFieldChange('isGeneralNote', e.target.value === 'true')}
                                                        className="edit-select"
                                                        title="Is General Note"
                                                    >
                                                        <option value="true">Yes</option>
                                                        <option value="false">No</option>
                                                    </select>
                                                ) : (
                                                    <span>{formData.isGeneralNote ? 'Yes' : 'No'}</span>
                                                )}
                                            </div>
                                            {!formData.isGeneralNote && (
                                                <div className="detail-item">
                                                    <label>Number</label>
                                                    {isInEditMode ? (
                                                        <EditableInput
                                                            value={formData.number || ''}
                                                            onChange={(e) => handleFieldChange('number', e.target.value)}
                                                            placeholder="Enter note number"
                                                        />
                                                    ) : (
                                                        <span>{formData.number}</span>
                                                    )}
                                                </div>
                                            )}
                                            <div className="detail-item">
                                                <label>Manufacturer</label>
                                                {isInEditMode ? (
                                                    <select
                                                        value={formData.manufacturerId || ''}
                                                        onChange={(e) => {
                                                            const selectedId = parseInt(e.target.value, 10);
                                                            const selectedManufacturer = manufacturers.find((m) => m.id === selectedId);
                                                            handleFieldChange('manufacturerId', selectedId);
                                                            handleFieldChange('manufacturerName', selectedManufacturer?.name || '');
                                                        }}
                                                        className="edit-select"
                                                        title="Manufacturer"
                                                    >
                                                        <option value="">-- Select Manufacturer --</option>
                                                        {manufacturers.map((m) => (
                                                            <option key={m.id} value={m.id}>
                                                                {m.name}
                                                            </option>
                                                        ))}
                                                    </select>
                                                ) : (
                                                    <span>{formData.manufacturerName || 'N/A'}</span>
                                                )}
                                            </div>
                                            <div className="detail-item">
                                                <label>External Id</label>
                                                <span>{formData.id}</span>
                                            </div>
                                        </div>
                                    </div>
                                    {!formData.isGeneralNote && (
                                        <div className="detail-section">
                                            <h4>Note Text</h4>
                                            <div className="detail-item full-width">
                                                {isInEditMode ? (
                                                    <EditableTextarea
                                                        value={formData.text || ''}
                                                        onChange={(e) => handleFieldChange('text', e.target.value)}
                                                        placeholder="Enter note text"
                                                        rows={10}
                                                    />
                                                ) : (
                                                    <div className="note-text" dangerouslySetInnerHTML={{ __html: formData.text || 'No text' }} />
                                                )}
                                            </div>
                                        </div>
                                    )}
                                    {formData.isGeneralNote && (
                                        <div className="detail-section">
                                            <h4>Documentation</h4>
                                            <div className="detail-item">
                                                {isInEditMode ? (
                                                    <>
                                                        <FileUploadRow>
                                                            <EditableInput
                                                                value={formData.filePath || ''}
                                                                onChange={(e) => handleFieldChange('filePath', e.target.value)}
                                                                placeholder="Enter file name"
                                                            />
                                                            <HiddenFileInput
                                                                ref={fileInputRef}
                                                                type="file"
                                                                onChange={handleFileUpload}
                                                                accept=".pdf,.doc,.docx,.txt"
                                                            />
                                                            <UploadButton
                                                                type="button"
                                                                onClick={handleUploadClick}
                                                                disabled={isUploading}
                                                                title="Upload Coating Documentation"
                                                            >
                                                                <i className="fas fa-upload"></i>
                                                                {isUploading ? 'Uploading...' : 'Upload'}
                                                            </UploadButton>
                                                        </FileUploadRow>
                                                        {uploadSuccess && (
                                                            <SuccessMessage>
                                                                <i className="fas fa-check-circle"></i> File uploaded successfully
                                                            </SuccessMessage>
                                                        )}
                                                        {uploadError && (
                                                            <ErrorMessage>
                                                                <i className="fas fa-exclamation-circle"></i> {uploadError}
                                                            </ErrorMessage>
                                                        )}
                                                    </>
                                                ) : formData.filePath ? (
                                                    <FileLink
                                                        href={getFileUrl('Coating', formData.filePath)}
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            handleDownload();
                                                        }}
                                                        title="Download coating documentation"
                                                    >
                                                        <i className="fas fa-file-pdf"></i>
                                                        {formData.filePath}
                                                        {isDownloading && <span>(Downloading...)</span>}
                                                    </FileLink>
                                                ) : (
                                                    <span>No file</span>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className="detail-placeholder">
                            <i className="fas fa-sticky-note"></i>
                            <h3>Select a coating note</h3>
                            <p>Choose a coating note from the list to view its details</p>
                        </div>
                    )}
                </DetailPanel>
            </ListDetailLayout>
        </TabContent>
    );
};

export default CoatingNoteTab;
